import React, { useState, useEffect } from 'react';
import { AdminService, SystemMetrics } from '../../services/adminService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';

const AdminAnalyticsDashboard: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'today' | '7d' | '30d' | '90d' | 'all'>('30d');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    loadMetrics();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getSystemMetrics(timeRange);
      setMetrics(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      showError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatCurrency = (num: number): string => {
    return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercentage = (num: number): string => {
    return num.toFixed(1) + '%';
  };

  const getChangeIndicator = (current: number, previous: number) => {
    if (previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    const isPositive = change >= 0;
    return (
      <span className={`text-xs flex items-center gap-1 ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        <svg className={`w-4 h-4 ${isPositive ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className={`px-4 py-2 rounded-lg border ${
              isDark
                ? 'bg-gray-800 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button
            onClick={loadMetrics}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</h3>
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(metrics.totalUsers)}</p>
            {getChangeIndicator(metrics.newUsersThisMonth, metrics.newUsersLastMonth)}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {metrics.activeUsers} active users
          </p>
        </div>

        {/* Total Revenue */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</h3>
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatCurrency(metrics.totalRevenue)}</p>
            {getChangeIndicator(metrics.revenueThisMonth, metrics.revenueLastMonth)}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            MRR: {formatCurrency(metrics.monthlyRecurringRevenue)}
          </p>
        </div>

        {/* Emails Sent */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Emails Sent</h3>
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(metrics.totalEmailsSent)}</p>
            {getChangeIndicator(metrics.emailsSentThisMonth, metrics.emailsSentLastMonth)}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {formatNumber(metrics.emailsSentToday)} sent today
          </p>
        </div>

        {/* Total Campaigns */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Campaigns</h3>
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(metrics.totalCampaigns)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {metrics.averageCampaignsPerUser.toFixed(1)} avg per user
          </p>
        </div>
      </div>

      {/* Revenue Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue Overview</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">This Month</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(metrics.revenueThisMonth)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Last Month</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(metrics.revenueLastMonth)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Last 3 Months</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(metrics.revenueLast3Months)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">This Year</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(metrics.revenueThisYear)}
              </span>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Revenue Per User</span>
                <span className="text-lg font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(metrics.averageRevenuePerUser)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Growth</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">New Users (This Month)</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(metrics.newUsersThisMonth)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">New Users (Last Month)</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(metrics.newUsersLastMonth)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Growth Rate</span>
              <span className={`text-lg font-semibold ${metrics.userGrowthRate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatPercentage(metrics.userGrowthRate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Churn Rate</span>
              <span className={`text-lg font-semibold ${metrics.churnRate <= 5 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatPercentage(metrics.churnRate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Retention Rate</span>
              <span className={`text-lg font-semibold ${metrics.retentionRate >= 80 ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                {formatPercentage(metrics.retentionRate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* User Subscription Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Subscription Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(metrics.paidUsers)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Paid Users</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{formatNumber(metrics.unpaidUsers)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Unpaid Users</p>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(metrics.freeUsers)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Free Users</p>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatNumber(metrics.premiumUsers)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Premium Users</p>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Email Engagement</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Open Rate</span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatPercentage(metrics.emailOpenRate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${Math.min(metrics.emailOpenRate, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Click Rate</span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatPercentage(metrics.emailClickRate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${Math.min(metrics.emailClickRate, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Unsubscribe Rate</span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatPercentage(metrics.unsubscribeRate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${metrics.unsubscribeRate > 2 ? 'bg-red-600' : 'bg-yellow-600'}`}
                  style={{ width: `${Math.min(metrics.unsubscribeRate * 10, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Bounce Rate</span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatPercentage(metrics.bounceRate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${metrics.bounceRate > 5 ? 'bg-red-600' : 'bg-green-600'}`}
                  style={{ width: `${Math.min(metrics.bounceRate * 10, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Engagement</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Avg Contacts per User</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(metrics.averageContactsPerUser)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Avg Campaigns per User</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {metrics.averageCampaignsPerUser.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Contacts</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(metrics.totalContacts)}
              </span>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Conversion Rate</span>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {formatPercentage(metrics.conversionRate)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Customer Lifetime Value</span>
              <span className="text-lg font-bold text-green-600 dark:text-green-400">
                {formatCurrency(metrics.customerLifetimeValue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Business Health */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Business Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-2xl font-bold text-green-700 dark:text-green-400">
              {formatCurrency(metrics.monthlyRecurringRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monthly Recurring Revenue</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Based on active subscriptions</p>
          </div>
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
              {formatCurrency(metrics.annualRecurringRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Annual Recurring Revenue</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">MRR × 12</p>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
              {formatPercentage(metrics.conversionRate)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Free → Paid Conversion</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Paid users / total users</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;

